const calendarLoads = new Map();

export function getEstimatedRanges(now, calendar, input = {}) {
  const settings = calendar.normalizeShippingSettings(input);
  const state = calendar.getDispatchState(now, settings);
  const dispatchStart = state.dispatchDate;
  const dispatchEnd = calendar.addPolishBusinessDays(state.processingDate, settings.dispatchMaxDays);
  return {
    localDate: state.localDate,
    dispatch: [dispatchStart, dispatchEnd],
    delivery: [calendar.addPolishBusinessDays(dispatchStart, settings.deliveryMinDays), calendar.addPolishBusinessDays(dispatchEnd, settings.deliveryMaxDays)]
  };
}

export function formatDateRange(range, locale = 'pl', currentYear = '') {
  const [start, end] = range;
  const first = new Date(`${start}T12:00:00Z`);
  const last = new Date(`${end}T12:00:00Z`);
  const options = { timeZone: 'Europe/Warsaw' };
  let formatter;
  try {
    formatter = new Intl.DateTimeFormat(locale, { ...options, day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    formatter = new Intl.DateTimeFormat('pl', { ...options, day: 'numeric', month: 'long', year: 'numeric' });
  }
  const firstParts = Object.fromEntries(formatter.formatToParts(first).map(({ type, value }) => [type, value]));
  const lastParts = Object.fromEntries(formatter.formatToParts(last).map(({ type, value }) => [type, value]));
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const years = sameYear ? (end.slice(0, 4) === String(currentYear) ? '' : lastParts.year) : `${firstParts.year} / ${lastParts.year}`;
  return {
    start,
    end,
    first: sameMonth ? firstParts.day : `${Number(start.slice(8, 10))}.${start.slice(5, 7)}`,
    last: sameMonth ? lastParts.day : `${Number(end.slice(8, 10))}.${end.slice(5, 7)}`,
    month: sameMonth ? [lastParts.month, years].filter(Boolean).join(' ') : years,
    accessible: start === end ? formatter.format(first) : `${formatter.format(first)} – ${formatter.format(last)}`
  };
}

const ElementBase = globalThis.HTMLElement || class {};

export class TapandaCartDateTimeline extends ElementBase {
  connectedCallback() {
    if (this.active) return;
    this.active = true;
    this.onVisibility = () => this.ownerDocument.hidden ? this.pause() : this.update();
    this.onPageShow = () => this.update();
    this.ownerDocument.addEventListener('visibilitychange', this.onVisibility);
    this.ownerDocument.defaultView.addEventListener('pageshow', this.onPageShow);
    if (this.calendar) return this.update();
    this.loadCalendar().then((calendar) => {
      if (!this.active || !this.isConnected) return;
      this.calendar = calendar;
      this.update();
    }).catch(() => {
      this.dataset.dateReady = 'false';
    });
  }

  loadCalendar() {
    const source = this.dataset.calendarSrc;
    if (!source) return Promise.reject(new Error('Missing business-day calendar.'));
    if (!calendarLoads.has(source)) calendarLoads.set(source, import(source));
    return calendarLoads.get(source);
  }

  disconnectedCallback() {
    this.active = false;
    this.pause();
    this.ownerDocument.removeEventListener('visibilitychange', this.onVisibility);
    this.ownerDocument.defaultView.removeEventListener('pageshow', this.onPageShow);
  }

  pause() {
    globalThis.clearTimeout(this.timer);
    this.timer = null;
  }

  renderRange(kind, range) {
    const days = this.querySelector(`[data-date-${kind}-days]`);
    const month = this.querySelector(`[data-date-${kind}-month]`);
    if (!days || !month) throw new Error('Missing timeline date nodes.');
    const first = this.ownerDocument.createElement('time');
    first.dateTime = range.start;
    first.textContent = range.first;
    const last = this.ownerDocument.createElement('time');
    last.dateTime = range.end;
    last.textContent = range.last;
    if (range.start === range.end) days.replaceChildren(first);
    else days.replaceChildren(first, this.ownerDocument.createTextNode('–'), last);
    days.setAttribute('aria-label', range.accessible);
    month.textContent = range.month;
  }

  update() {
    this.pause();
    if (!this.active || !this.isConnected || !this.calendar || this.ownerDocument.hidden) return;
    try {
      const ranges = getEstimatedRanges(new Date(), this.calendar, this.dataset);
      const locale = this.dataset.locale || 'pl';
      const signature = `${locale}:${ranges.dispatch.join(':')}:${ranges.delivery.join(':')}`;
      if (signature !== this.lastSignature) {
        const year = ranges.localDate.slice(0, 4);
        this.renderRange('dispatch', formatDateRange(ranges.dispatch, locale, year));
        this.renderRange('delivery', formatDateRange(ranges.delivery, locale, year));
        this.dataset.dateReady = 'true';
        this.lastSignature = signature;
      }
      this.timer = globalThis.setTimeout(() => this.update(), 60000 - Date.now() % 60000 + 25);
    } catch {
      this.dataset.dateReady = 'false';
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('tapanda-cart-date-timeline')) {
  globalThis.customElements.define('tapanda-cart-date-timeline', TapandaCartDateTimeline);
}

// The product badge uses the same calendar and cutoff as both cart presentations.
export function getDispatchSummary(state, settings, messages) {
  if (settings.dispatchMinDays === 0 && settings.dispatchMaxDays === 0) return messages.within24h;
  const offset = state.period === 'closed' ? 1 : 0;
  const min = settings.dispatchMinDays + offset;
  const max = settings.dispatchMaxDays + offset;
  let message;
  if (min === 0 && max === 0) message = messages.today;
  else if (min === 0 && max === 1) message = messages.withinOne;
  else if (min === 0) message = messages.withinMany;
  else if (min === 1 && max === 1) message = messages.one;
  else if (min === max) message = messages.many;
  else message = messages.range;
  return message.replace('__min__', String(min)).replace('__max__', String(max));
}

// Product-only date presentation. The cart's preparation/transit ranges stay unchanged.
export function getDispatchSummaryDate(now, calendar, input = {}, locale = 'pl') {
  const settings = calendar.normalizeShippingSettings(input);
  const state = calendar.getDispatchState(now, settings);
  let start = state.dispatchDate;
  let end = calendar.addPolishBusinessDays(state.processingDate, settings.dispatchMaxDays);
  if (settings.dispatchMinDays === 0 && settings.dispatchMaxDays === 0) {
    const localBusinessDay = calendar.isPolishBusinessDay(state.localDate);
    // Before cutoff: today or next working day. On a closed day: the next two.
    // After cutoff on a working day: only the next working day.
    if (state.period !== 'closed' || !localBusinessDay) end = calendar.nextPolishBusinessDay(start);
  }
  const year = state.localDate.slice(0, 4);
  const options = { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long' };
  if (start.slice(0, 4) !== year || end.slice(0, 4) !== year) options.year = 'numeric';
  let formatter;
  try {
    formatter = new Intl.DateTimeFormat(locale, options);
  } catch {
    formatter = new Intl.DateTimeFormat('pl', options);
  }
  const first = new Date(start + 'T12:00:00Z');
  const last = new Date(end + 'T12:00:00Z');
  if (start === end) return formatter.format(first);
  return typeof formatter.formatRange === 'function'
    ? formatter.formatRange(first, last)
    : formatter.format(first) + ' – ' + formatter.format(last);
}

export class TapandaDispatchSummary extends TapandaCartDateTimeline {
  update() {
    this.pause();
    if (!this.active || !this.isConnected || !this.calendar || this.ownerDocument.hidden) return;
    try {
      const settings = this.calendar.normalizeShippingSettings(this.dataset);
      const locale = this.dataset.locale || this.ownerDocument.documentElement?.lang || 'pl';
      const text = getDispatchSummaryDate(new Date(), this.calendar, settings, locale);
      const node = this.querySelector('[data-dispatch-summary]');
      if (node && node.textContent !== text) node.textContent = text;
      this.dataset.dateReady = 'true';
      this.timer = globalThis.setTimeout(() => this.update(), 60000 - Date.now() % 60000 + 25);
    } catch {
      this.dataset.dateReady = 'false';
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('tapanda-dispatch-summary')) {
  globalThis.customElements.define('tapanda-dispatch-summary', TapandaDispatchSummary);
}
